import { createApp, defineComponent } from 'vue';
import { getParentContainer } from './utils';

// Eagerly import all behavioral code components
export const initializers = import.meta.glob('../../js-initializers/**/*.ts', { eager: true });

//*
// Dynamically load and mount behavioral Vue components based on the presence of elements with the `data-initializer` attribute.
//
export function loadInitializers(root: Document | HTMLElement = document) {
    let elements = root.querySelectorAll('[data-initializer]') as NodeListOf<HTMLElement>;
    elements.forEach((elem) => {
        // get the component name from the data attribute
        const componentName = elem.getAttribute('data-initializer')!;

        // get a reference for the container element (same element or its parent)
        const container = elem?.tagName === 'TEMPLATE' || elem?.tagName === 'SCRIPT' ? elem.parentElement! : elem;

        // get the component properties from the element attributes or script content, depending on the tag type
        const props = elem.tagName === 'SCRIPT' ? getScriptProps(elem) : getElementProps(elem);

        // dynamically get the init function
        const init = (initializers[`../../js-initializers/${componentName}.ts`] ||
            initializers[`../../js-initializers/_shared/${componentName}.ts`]) as any;

        // invoke the function
        if (init) {
            init.default(container, props);
        }
    });
}

/**
 * Get properties for a component from a script tag content, parsing it as JSON
 * @returns an object with the parsed properties
 */
export function getScriptProps(elem: HTMLElement) {
    return JSON.parse(elem.textContent || '{}');
}

/**
 * Get properties for a component from an element tag, mapping data attributes to an object
 * @returns an object with the mapped properties
 */
export function getElementProps(elem: HTMLElement) {
    const props: Record<string, any> = {};
    Object.keys(elem.dataset).forEach((key) => {
        if (key !== 'initializer' && key !== 'loadComponent') {
            props[key] = elem.dataset[key]!;
        }
    });
    return props;
}
